import React, { useState, useEffect, useCallback } from 'react';
import IndustrySelect from './common/IndustrySelect';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const formatClientDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
        console.error("Error formatting date:", error, "Timestamp:", timestamp);
        return 'Invalid Date';
    }
};

const isValidEmail = (email) => {
    if (!email || email.trim() === '') return true;
    return /\S+@\S+\.\S+/.test(email);
};

export default function ClientInformationView({ clientId, client, user, onClientUpdate }) {
    const navigate = useNavigate();
    const isOwner = client?.fcfoId === user?.uid || client?.userId === user?.uid;
    const [loading, setLoading] = useState(true);
    const [showClientInfoHelp, setShowClientInfoHelp] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [editClientName, setEditClientName] = useState('');
    const [editWebsiteUrl, setEditWebsiteUrl] = useState('');
    const [editClientContactName, setEditClientContactName] = useState('');
    const [editClientEmail, setEditClientEmail] = useState('');
    const [editClientPhone, setEditClientPhone] = useState('');
    const [editClientIndustry, setEditClientIndustry] = useState('');
    const [editClientNotes, setEditClientNotes] = useState('');
    const [editClientActive, setEditClientActive] = useState(true);
    const [showStatusChangeConfirmation, setShowStatusChangeConfirmation] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

const handleDeleteClient = async () => {
    if (!client) {
        toast.error("No client selected.");
        return;
    }
    setIsDeleting(true);
    const toastId = toast.loading(`Deleting client "${client.name}"...`);
    try {
        const deleteClient = httpsCallable(functions, 'deleteClientAndAssociatedData');
        await deleteClient({ clientId: client.id });

        toast.update(toastId, {
            render: `Successfully deleted client "${client.name}"`,
            type: "success",
            isLoading: false,
            autoClose: 5000,
    });
        setIsDeleteDialogOpen(false);
        navigate('/client-dashboard/manage'); // Navigate back to the main list view
    } catch (err) {
        console.error("Error deleting client:", err);
        toast.update(toastId, {
            render: `Failed to delete client: ${err.message}`,
            type: "error",
            isLoading: false,
            autoClose: 5000,
        });
    } finally {
        setIsDeleting(false);
    }
};
    const [pendingStatusChange, setPendingStatusChange] = useState({ newStatus: true, clientName: '' });
    const NEW_CLIENT_MONTHLY_COST = 95;

    const populateForm = useCallback((client) => {
  if (client) {
    setEditClientName(client.companyName || client.name || '');
    setEditWebsiteUrl(client.websiteUrl || client.submittedForAdminReview?.websiteUrl || '');
    setEditClientContactName(client.contactName || '');
    setEditClientEmail(client.email || '');
    setEditClientPhone(client.phone || '');
    setEditClientIndustry(client.industry || '');
    setEditClientNotes(client.notes || '');
    setEditClientActive(client.isActive === true);
  }
}, []);

    useEffect(() => {
  // When the client prop from the parent changes, populate the form.
  if (client) {
    populateForm(client);
    setLoading(false);
    setError('');
  } else {
    // If no client is selected, ensure we show the loading or empty state.
    setLoading(!clientId);
  }
}, [client, clientId, populateForm]);

    const handleEdit = () => {
        if (client) {
            populateForm(client);
        }
        setIsEditing(true);
        setError('');
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (client) {
            populateForm(client);
        }
        setError('');
    };

    const handleStatusToggleRequest = (newStatus) => {
        if (!client) return;
        setPendingStatusChange({ newStatus, clientName: client.name });
        setShowStatusChangeConfirmation(true);
    };

    const confirmStatusChange = () => {
        if (pendingStatusChange === null || !client) return;
        setEditClientActive(pendingStatusChange.newStatus);
        setShowStatusChangeConfirmation(false);
        toast.info(`Status for ${client.name} will be changed to ${pendingStatusChange.newStatus ? 'Active' : 'Inactive'} once you click "Save Changes".`);
    };

    const handleSaveChanges = async (e) => {
        if (e) e.preventDefault();
        if (!editClientName.trim()) {
            toast.error('Client name is required.');
            setError('Client name is required.');
            return;
        }
        if (editClientEmail.trim() && !isValidEmail(editClientEmail)) {
            toast.error('Please enter a valid email address.');
            setError('Valid email address is required.');
            return;
        }
        if (!clientId || !client) {
            toast.error('No client selected or client data missing.');
            setError('No client selected or client data missing.');
            return;
        }

        setFormSubmitting(true);
        setError('');
        const previousActiveStatus = client.isActive;
        try {
    // NOTE: The logic to build the data object is slightly different
    // to match what our new backend function expects.
    const previousActiveStatus = client.isActive;
    const updatedData = {
        name: editClientName.trim(),
        companyName: editClientName.trim(),
        websiteUrl: editWebsiteUrl.trim(),
        contactName: editClientContactName.trim(),
        email: editClientEmail.trim(),
        phone: editClientPhone.trim(),
        industry: editClientIndustry.trim(),
        notes: editClientNotes.trim(),
        isActive: editClientActive,
    };
    
    // Call the new secure backend function instead of writing directly
    const updateClientDetails = httpsCallable(functions, 'updateClientDetails');
    await updateClientDetails({ 
        clientId: clientId, 
        updatedData: updatedData 
    });

    setIsEditing(false);
    toast.success(`Client "${editClientName.trim()}" updated successfully.`);
    
    // Check if status changed to provide specific user feedback
    const statusActuallyChanged = previousActiveStatus !== editClientActive;
    if (statusActuallyChanged) {
        if (editClientActive === false) {
            toast.info(`Client deactivated. Billing will be adjusted on your next invoice.`);
        } else {
            toast.info(`Client reactivated. Billing will be adjusted accordingly.`);
        }
    }
if (statusActuallyChanged) {
    if (editClientActive === false) {
    toast.info(`Client "${editClientName.trim()}" deactivated. Billing adjustment may apply next cycle.`);
    } else {
    toast.info(`Client "${editClientName.trim()}" reactivated. This may affect billing.`);
    }
}
             if (pendingStatusChange) {
                setPendingStatusChange(null);
            }

            if (onClientUpdate) {
                console.log("ClientInformationView: Calling onClientUpdate after save.");
                onClientUpdate();
            }

        } catch (err) {
    console.error('Error updating client:', err);
    setError('Failed to update client information. Please try again.');
    
    // Check if this is a payment method error when trying to reactivate
    const errorMessage = err.message || '';
    const isReactivating = previousActiveStatus === false && editClientActive === true;
    
    if (isReactivating && (errorMessage.includes('payment') || errorMessage.includes('subscription'))) {
        toast.error('Please add a payment method in the Account tab before reactivating clients.');
    } else {
        toast.error('Failed to update client information.');
    }
} finally {
    setFormSubmitting(false);
}
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, minHeight: 200 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Client Information...</Typography>
            </Box>
        );
    }

    if (error && !client) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    
    if (!client && !loading) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center', m:2 }}>
                <Typography color="text.secondary">No client data to display for the selected client ID.</Typography>
            </Paper>
        );
    }

    const renderStatusChangeConfirmationDialog = () => (
        <Dialog 
            open={showStatusChangeConfirmation} 
            onClose={() => !formSubmitting && setShowStatusChangeConfirmation(false)}
        >
            <DialogTitle>
                {pendingStatusChange?.newStatus ? "Activate Client" : "Deactivate Client"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    {pendingStatusChange?.newStatus
                        ? <>
                            Are you sure you want to activate client "{pendingStatusChange?.clientName || 'this client'}"?
                            This will resume monthly billing (${NEW_CLIENT_MONTHLY_COST}) for this client, effective from your next billing cycle.
                            <br /><br />
                            <strong>After clicking 'Confirm' below, you must click the 'Save Changes' button on the form to finalize this change.</strong>
                          </>
                        : <>
                            Are you sure you want to deactivate client "{pendingStatusChange?.clientName || 'this client'}"?
                            This client will be marked as inactive and billing will be paused, effective from your next billing cycle.
                            <br /><br />
                            <strong>After clicking 'Confirm' below, you must click the 'Save Changes' button on the form to finalize this change.</strong>
                          </>
                    }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {setShowStatusChangeConfirmation(false);}} disabled={formSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={confirmStatusChange}
                    color={pendingStatusChange?.newStatus ? "primary" : "warning"}
                    variant="contained"
                    disabled={formSubmitting}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Paper elevation={0} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {renderStatusChangeConfirmationDialog()}
            
            {error && isEditing && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                        {!isEditing && client && (
                <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                        <Typography variant="h6" component="h3">Client Information</Typography>
                        <IconButton size="small" onClick={() => setShowClientInfoHelp(prev => !prev)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Collapse in={showClientInfoHelp}>
                        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowClientInfoHelp(false)}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Client Details</strong> shows the core information for this client. Click Edit Client Info to update company name, website, contact details, industry, and notes.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Status</strong> controls whether this client is Active or Inactive. Inactive clients do not count toward your subscription and AI features are disabled for them.
                            </Typography>
                            <Typography variant="body2">
                                <strong>Financial Intelligence Tier</strong> reflects the quality of financial data available. Full Financial Intelligence requires QuickBooks or a PDF upload. Estimated Ranges use the quick-estimate inputs in the Financials tab.
                            </Typography>
                        </Alert>
                    </Collapse>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h6" component="h3" gutterBottom>
    Client Details: {editClientName}
</Typography>
<Button
    variant="outlined"
    size="small"
    onClick={handleEdit}
    disabled={formSubmitting}
    sx={{mb: {xs: 1, sm: 0}}}
>
    Edit Client Info
</Button>
</Box>
{editClientActive === false && (
    <Alert severity="warning" sx={{ mb: 2 }}>
    This client is currently Inactive. Editing is limited.
    </Alert>
)}
<Grid container spacing={2}>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Company Name</Typography>
    <Typography variant="body1">{editClientName || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Website URL</Typography>
    <Typography variant="body1">{editWebsiteUrl || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Contact Person</Typography>
    <Typography variant="body1">{editClientContactName || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
    <Typography variant="body1">{editClientEmail || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
    <Typography variant="body1">{editClientPhone || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Industry</Typography>
    <Typography variant="body1">{editClientIndustry || '-'}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
    <Typography variant="body1" sx={{ color: editClientActive === false ? 'error.main' : 'success.main', fontWeight: 'medium' }}>
        {editClientActive === false ? 'Inactive' : 'Active'}
    </Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Profile Created</Typography>
    <Typography variant="body1">{formatClientDate(client?.createdAt)}</Typography>
    </Grid>
    <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">Last Status Change</Typography>
    <Typography variant="body1">{formatClientDate(client?.statusChangedAt) || 'N/A'}</Typography>
    </Grid>
    {editClientNotes && (
    <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" display="block">Notes</Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{editClientNotes}</Typography>
    </Grid>
    )}
                    </Grid>
                </Box>
            )}

            {isEditing && client && (
                <Box component="form" onSubmit={handleSaveChanges} noValidate>
                    <Typography variant="h6" component="h3" gutterBottom sx={{mb: 2}}>
                        Edit Client: {editClientName || client.name}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Company Name"
                                value={editClientName}
                                onChange={(e) => setEditClientName(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                                error={!editClientName.trim() && !!error}
                                helperText={!editClientName.trim() && !!error ? "Company name is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Website URL"
                                value={editWebsiteUrl}
                                onChange={(e) => setEditWebsiteUrl(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                                placeholder="e.g., www.company.com"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Contact Person"
                                value={editClientContactName}
                                onChange={(e) => setEditClientContactName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={editClientEmail}
                                onChange={(e) => setEditClientEmail(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                                error={editClientEmail.trim() && !isValidEmail(editClientEmail)}
                                helperText={editClientEmail.trim() && !isValidEmail(editClientEmail) ? "Invalid email format" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Phone"
                                type="tel"
                                value={editClientPhone}
                                onChange={(e) => setEditClientPhone(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
  <IndustrySelect
    value={editClientIndustry}
    onChange={(industry) => setEditClientIndustry(industry)}
    organizationId={user?.organizationId}
    size="small"
    disabled={formSubmitting}
    label="Industry"
    required={false}
  />
</Grid>
                         <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editClientActive}
                                        onChange={(e) => handleStatusToggleRequest(e.target.checked)}
                                        name="clientActive"
                                        color="primary"
                                        disabled={formSubmitting}
                                    />
                                }
                                label={editClientActive ? "Client is Active" : "Client is Inactive"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Notes"
                                value={editClientNotes}
                                onChange={(e) => setEditClientNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                size="small"
                                disabled={formSubmitting}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCancelEdit} disabled={formSubmitting} color="inherit">
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={formSubmitting}>
          {formSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  )}

  {isOwner && (
    <>
      <Divider sx={{ my: 3, borderColor: 'error.light' }}>
        <Typography variant="overline" color="error">Danger Zone</Typography>
      </Divider>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
              variant="outlined"
              color="error"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={formSubmitting}
          >
              Delete Client
          </Button>
      </Box>
    </>
  )}

  {/* Delete Confirmation Dialog */}
  <Dialog
      open={isDeleteDialogOpen}
      onClose={() => setIsDeleteDialogOpen(false)}
  >
      <DialogTitle>Confirm Client Deletion</DialogTitle>
      <DialogContent>
          <DialogContentText>
              Are you sure you want to permanently delete the client "{client?.name}"?
              This action cannot be undone and will delete all associated documents and data.
          </DialogContentText>
      </DialogContent>
      <DialogActions>
    <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
    <Button onClick={handleDeleteClient} color="error" disabled={isDeleting}>
        {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Delete Client'}
    </Button>
</DialogActions>
  </Dialog>
</Paper>
);
}